/**
 * Privacy Policy — production legal text satisfying Paddle verification and
 * GDPR/CCPA-aligned disclosures. Covers data collection, cookies, analytics,
 * authentication, Paddle payments, Resend emails, user rights, retention,
 * contact information.
 */
import React from "react";
import { LegalPageLayout, LegalSection } from "./LegalPageLayout";

const SECTIONS = [
  { id: "overview", title: "1. Overview" },
  { id: "data-we-collect", title: "2. Data We Collect" },
  { id: "how-we-use", title: "3. How We Use Data" },
  { id: "cookies", title: "4. Cookies & Analytics" },
  { id: "ai-data", title: "5. AI Data Handling" },
  { id: "payments", title: "6. Payments (Paddle)" },
  { id: "emails", title: "7. Emails (Resend)" },
  { id: "sharing", title: "8. Data Sharing & Sub-processors" },
  { id: "security", title: "9. Security" },
  { id: "retention", title: "10. Data Retention" },
  { id: "your-rights", title: "11. Your Rights" },
  { id: "international", title: "12. International Transfers" },
  { id: "children", title: "13. Children's Privacy" },
  { id: "changes", title: "14. Changes to This Policy" },
  { id: "contact", title: "15. Contact" },
];

export default function PrivacyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      subtitle="How Ashra AI collects, uses, and protects your information."
      lastUpdated="May 29, 2026"
      sections={SECTIONS}
    >
      <LegalSection id="overview" title="1. Overview">
        <p>
          This Privacy Policy explains how <strong>Ashra AI</strong> (“we”, “us”,
          “our”) collects, uses, shares and protects information when you visit
          <a href="https://ashraai.com"> ashraai.com</a>, create an account, or use
          our Service. We are committed to handling your data lawfully, fairly,
          and transparently.
        </p>
        <p>
          By using the Service, you agree to the practices described in this
          Privacy Policy. If you do not agree, please do not use the Service.
        </p>
      </LegalSection>

      <LegalSection id="data-we-collect" title="2. Data We Collect">
        <h3>2.1 Account & profile data</h3>
        <ul>
          <li>Name, email address, password (stored as a bcrypt hash, never plaintext)</li>
          <li>Workspace and business profile information you provide</li>
          <li>Authentication tokens and session identifiers</li>
        </ul>
        <h3>2.2 Customer & contact data you upload</h3>
        <ul>
          <li>Leads, contacts, customer profiles, conversations, notes, tags</li>
          <li>Email lists imported into the Service</li>
        </ul>
        <h3>2.3 Usage & technical data</h3>
        <ul>
          <li>IP address, browser type, device information, operating system</li>
          <li>Pages visited, features used, AI conversation counts, timestamps</li>
          <li>Performance metrics, error logs, application telemetry</li>
        </ul>
        <h3>2.4 Payment data</h3>
        <p>
          We <strong>do not store</strong> your payment card details. All payments
          are processed by Paddle (see Section 6). We receive only billing-related
          metadata: customer ID, subscription status, last 4 digits of payment
          method, country, and invoice references.
        </p>
        <h3>2.5 Communications</h3>
        <ul>
          <li>Emails, support messages and chat transcripts you send to us</li>
          <li>Survey responses, feedback, in-product comments</li>
        </ul>
      </LegalSection>

      <LegalSection id="how-we-use" title="3. How We Use Data">
        <p>We process personal data for the following purposes:</p>
        <ul>
          <li><strong>Provide the Service</strong> — authenticate you, deliver features, run AI agents on your behalf;</li>
          <li><strong>Billing &amp; subscriptions</strong> — manage your plan, renewals, invoices and refunds (via Paddle);</li>
          <li><strong>Communications</strong> — send transactional emails (welcome, login codes, purchase confirmations, password resets) and, with your consent, marketing emails;</li>
          <li><strong>Improve the Service</strong> — analytics, debugging, performance optimization, security monitoring;</li>
          <li><strong>Comply with the law</strong> — respond to legal requests, prevent fraud, enforce our Terms.</li>
        </ul>
        <p>
          <strong>Legal basis (GDPR):</strong> performance of the contract with you,
          our legitimate interests in operating and improving the Service, your
          consent (for marketing communications), and compliance with legal
          obligations.
        </p>
      </LegalSection>

      <LegalSection id="cookies" title="4. Cookies & Analytics">
        <p>
          We use cookies and similar technologies to keep you logged in, remember
          preferences, and understand how the Service is used. Cookie categories:
        </p>
        <ul>
          <li><strong>Strictly necessary</strong> — session cookies for authentication. Always enabled.</li>
          <li><strong>Functional</strong> — UI preferences (theme, layout).</li>
          <li><strong>Analytics</strong> — aggregated usage data; never sold to third parties.</li>
        </ul>
        <p>
          You can disable non-essential cookies in your browser settings. Disabling
          essential cookies will prevent the Service from functioning correctly.
        </p>
      </LegalSection>

      <LegalSection id="ai-data" title="5. AI Data Handling">
        <p>
          When you use AI features (Hunter, Closer, Recovery, Strategist, Forge),
          your prompts and the relevant workspace context are sent to third-party
          AI model providers (currently OpenAI, with future expansion to Anthropic
          and Google) for processing.
        </p>
        <ul>
          <li>Outputs are returned to your workspace and stored alongside the original prompt.</li>
          <li>We do not use your customer data to train third-party AI models.</li>
          <li>OpenAI's API terms confirm that API content is not used to train their models.</li>
          <li>Anonymous, aggregated usage statistics may be used to improve the Service.</li>
        </ul>
      </LegalSection>

      <LegalSection id="payments" title="6. Payments (Paddle)">
        <p>
          Subscriptions are processed by <strong>Paddle.com Market Ltd</strong>
          (“Paddle”), our Merchant of Record. Paddle is responsible for:
        </p>
        <ul>
          <li>collecting and storing your payment information (card, PayPal, etc.);</li>
          <li>calculating and remitting sales tax, VAT, GST in your jurisdiction;</li>
          <li>issuing invoices and processing refunds;</li>
          <li>fraud prevention and PCI-DSS compliance.</li>
        </ul>
        <p>
          We share only the minimum information required to set up your
          subscription (email, plan, workspace ID). For Paddle's data handling,
          see <a href="https://www.paddle.com/legal/privacy">Paddle's Privacy Policy</a>.
        </p>
      </LegalSection>

      <LegalSection id="emails" title="7. Emails (Resend)">
        <p>
          Transactional and marketing emails are delivered through
          <strong> Resend</strong> (Resend Inc.). Resend processes recipient email
          addresses, subject lines, message bodies and delivery metadata
          (delivered, opened, bounced, complained) to send and report on emails on
          our behalf.
        </p>
        <p>
          You may unsubscribe from marketing emails at any time using the
          unsubscribe link in any message footer or by emailing
          <a href="mailto:privacy@ashraai.com"> privacy@ashraai.com</a>.
          Transactional messages (login codes, purchase receipts, password resets)
          are necessary to operate the Service and cannot be turned off while you
          remain a subscriber.
        </p>
      </LegalSection>

      <LegalSection id="sharing" title="8. Data Sharing & Sub-processors">
        <p>We share personal data only with the following categories of recipients:</p>
        <ul>
          <li><strong>Sub-processors</strong> who operate the Service on our behalf — Paddle (payments), Resend (email), OpenAI (AI inference), MongoDB Atlas (database hosting), our cloud infrastructure provider.</li>
          <li><strong>Legal authorities</strong> when required by law, court order, or to protect our rights or the safety of users.</li>
          <li><strong>Successors</strong> in the event of a merger, acquisition, or asset sale — your data may transfer subject to this Policy.</li>
        </ul>
        <p>
          <strong>We do not sell, rent or trade your personal data.</strong>
        </p>
      </LegalSection>

      <LegalSection id="security" title="9. Security">
        <p>
          We implement administrative, technical and physical safeguards designed
          to protect your data, including:
        </p>
        <ul>
          <li>Encrypted connections (HTTPS/TLS) for all traffic;</li>
          <li>Passwords stored as bcrypt hashes;</li>
          <li>JWT-based session management with short expiry;</li>
          <li>Database access restricted to authorized personnel and services;</li>
          <li>Per-workspace data isolation in all queries;</li>
          <li>Regular security audits and dependency updates.</li>
        </ul>
        <p>
          No method of transmission or storage is 100% secure. You are responsible
          for keeping your account credentials safe.
        </p>
      </LegalSection>

      <LegalSection id="retention" title="10. Data Retention">
        <ul>
          <li><strong>Account data:</strong> retained while your account is active.</li>
          <li><strong>Customer / contact data:</strong> retained for as long as your account exists; deleted within 30 days of account closure unless legally required to retain.</li>
          <li><strong>Email logs &amp; audit logs:</strong> retained 90 days (TTL).</li>
          <li><strong>Billing records (Paddle):</strong> retained per Paddle's policy and applicable tax law (typically 7 years).</li>
          <li><strong>Backups:</strong> rolling 30-day window, then permanently deleted.</li>
        </ul>
      </LegalSection>

      <LegalSection id="your-rights" title="11. Your Rights">
        <p>Subject to applicable law, you have the right to:</p>
        <ul>
          <li><strong>Access</strong> the personal data we hold about you;</li>
          <li><strong>Correct</strong> inaccurate or incomplete data;</li>
          <li><strong>Delete</strong> your data (subject to legal retention requirements);</li>
          <li><strong>Export</strong> a copy of your data in a portable format;</li>
          <li><strong>Restrict or object</strong> to certain processing;</li>
          <li><strong>Withdraw consent</strong> for marketing communications at any time;</li>
          <li><strong>Lodge a complaint</strong> with your local data protection authority.</li>
        </ul>
        <p>
          To exercise any of these rights, contact
          <a href="mailto:privacy@ashraai.com"> privacy@ashraai.com</a>. We respond
          within 30 days.
        </p>
        <h3>11.1 California residents (CCPA/CPRA)</h3>
        <p>
          California residents have the right to know what categories of personal
          information we collect, the right to delete, the right to opt out of the
          “sale” or “sharing” of personal information (we do neither), and the
          right not to be discriminated against for exercising these rights.
        </p>
      </LegalSection>

      <LegalSection id="international" title="12. International Data Transfers">
        <p>
          Ashra AI is operated from the United States. By using the Service, you
          consent to your data being transferred to and processed in the United
          States and other jurisdictions where our sub-processors operate. We rely
          on Standard Contractual Clauses (SCCs) and equivalent safeguards for
          transfers from the European Economic Area, United Kingdom and
          Switzerland.
        </p>
      </LegalSection>

      <LegalSection id="children" title="13. Children's Privacy">
        <p>
          The Service is not intended for individuals under the age of 18. We do
          not knowingly collect personal data from children. If you believe a
          child has provided us personal data, please contact
          <a href="mailto:privacy@ashraai.com"> privacy@ashraai.com</a> and we will
          delete it.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="14. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. The "Last updated"
          date at the top of this page indicates when the latest version was
          published. For material changes we will provide at least thirty (30)
          days' notice before changes take effect.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="15. Contact">
        <p>
          Questions about this Privacy Policy or your data:
        </p>
        <ul>
          <li><strong>Privacy:</strong> <a href="mailto:privacy@ashraai.com">privacy@ashraai.com</a></li>
          <li><strong>Legal:</strong> <a href="mailto:legal@ashraai.com">legal@ashraai.com</a></li>
          <li><strong>Support:</strong> <a href="mailto:support@ashraai.com">support@ashraai.com</a></li>
        </ul>
      </LegalSection>
    </LegalPageLayout>
  );
}
